import { goto, invalidate } from '$app/navigation';
import { page } from '$app/state';
import { PUBLIC_POCKETBASE_URL } from '$env/static/public';
import PocketBase from 'pocketbase';
import {
	COL_ANALYTICS_USERS,
	COL_API_KEYS,
	COL_CONTENT_SUMMARY,
	COL_CONVERSATION,
	COL_TENANT,
	COL_USERS
} from 'src/constants/collections';
import type { ApiKeyRecord, CreateApiKeyData } from 'src/types/api-key';
import type { ConversationRecord } from 'src/types/conversation';
import type { ChatMessages } from 'src/types/llm';
import type { TenantRecord } from 'src/types/tenant';
import type { UserRecord, UserWithStats } from 'src/types/user';

/** Extension of Pocketbase Service. Used on the server and on the client */
export class PbService extends PocketBase {
	// We need to access /api/ob from the client as we dont have access to the auth cookie.
	// sveltekit backend then proxies the request to pb adding the auth token
	constructor(
		baseURL = PUBLIC_POCKETBASE_URL,
		private apiKey?: string
	) {
		super(baseURL);

		// If API key is provided, add it to all requests
		if (apiKey) {
			this.beforeSend = (url, options) => {
				options.headers = {
					...options.headers,
					Authorization: `Bearer ${apiKey}`
				};
				options.credentials = 'omit';
				return { url, options };
			};
		} else {
			this.beforeSend = (url, options) => {
				options.credentials = 'include';
				return { url, options };
			};
		}
	}

	async updateConversation(id: string, update: Partial<ConversationRecord>) {
		this.collection<ConversationRecord>(COL_CONVERSATION).update(id, update);
		await invalidate(`update:conversation:${id}`);
		await invalidate('update:conversations');
	}

	async updateMyself(changes: Partial<UserRecord>) {
		await this.collection<UserRecord>(COL_USERS).update(page.data.loggedInUser.id, changes);
		await invalidate('update:user');
	}

	async deleteMyAccount() {
		await this.collection<UserRecord>(COL_USERS).update(page.data.loggedInUser.id, {
			deleted_at: new Date().toISOString()
		});
	}

	async updateUserRole(userId: string, role: string): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, { role });
		await invalidate('update:users');
	}

	async updateUserPlan(userId: string, plan: string): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, { plan });
		await invalidate('update:users');
	}

	async deleteUser(userId: string): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, {
			deleted_at: new Date().toISOString()
		});
		await invalidate('update:users');
	}

	async updateTenant(tenantId: string, changes: Partial<TenantRecord>) {
		await this.collection<TenantRecord>(COL_TENANT).update(tenantId, changes);
		await invalidate('update:tenant');
	}
	async trackViewState(viewStateKey: 'onboarding'): Promise<void>;
	async trackViewState(viewStateKey: 'dailyBriefing', subKey: string): Promise<void>;
	async trackViewState(viewStateKey: keyof UserRecord['viewState'], subKey?: string) {
		const viewStateUpdate: UserRecord['viewState'] = {
			...(page.data.loggedInUser.viewState || {})
		};

		if (viewStateKey === 'dailyBriefing') {
			viewStateUpdate[viewStateKey] = {
				...(viewStateUpdate[viewStateKey] || {}),
				[subKey]: new Date()
			};
		} else {
			viewStateUpdate[viewStateKey] = new Date();
		}

		await this.updateMyself({
			viewState: viewStateUpdate
		});
	}

	async deleteConvo(id: string) {
		await this.collection<ConversationRecord>(COL_CONVERSATION).delete(id);
		await invalidate('update:conversations');
		if (page.params?.conversationId === id) {
			goto('/');
		}
	}

	async requestResetPassword(email: string): Promise<void> {
		await this.collection(COL_USERS).requestPasswordReset(email);
	}
	async saveNewPassword(password: string, passwordConfirm: string, token: string): Promise<void> {
		await this.collection(COL_USERS).confirmPasswordReset(token, password, passwordConfirm);
	}

	async getLatestBriefingId(tenantId: string) {
		const { id: latestBriefingId } = await this.collection<{ id: string }>(
			COL_CONTENT_SUMMARY
		).getFirstListItem(
			this.filter(`tenant = {:tenantId} && summary_type = 'daily_briefing'`, {
				tenantId
			}),
			{
				sort: '-created',
				fields: 'id'
			}
		);

		return latestBriefingId;
	}

	async listApiKeys(tenantId: string): Promise<ApiKeyRecord[]> {
		const records = await this.collection<ApiKeyRecord>(COL_API_KEYS).getFullList({
			filter: this.filter(`tenant = {:tenantId}`, { tenantId }),
			sort: '-created'
		});
		return records;
	}

	async createApiKey(data: CreateApiKeyData): Promise<ApiKeyRecord> {
		// Key is generated server-side via backend hook
		const record = await this.collection<ApiKeyRecord>(COL_API_KEYS).create({
			...data,
			is_active: true,
			usage_count: 0
		});
		await invalidate('update:api_keys');
		return record;
	}

	async deleteApiKey(id: string): Promise<void> {
		await this.collection<ApiKeyRecord>(COL_API_KEYS).delete(id);
		await invalidate('update:api_keys');
	}

	async getTenant(tenantId: string): Promise<TenantRecord> {
		const record = await this.collection<TenantRecord>(COL_TENANT).getOne(tenantId, {
			fields: 'id, name, welcome_message, feature_flags, default_model'
		});
		return record;
	}

	async createTenant(data: Partial<TenantRecord>): Promise<TenantRecord> {
		const record = await this.collection<TenantRecord>(COL_TENANT).create(data);
		await invalidate('update:tenants');
		return record;
	}

	async fetchUsersWithStats(
		tenantId: string,
		search: string,
		page: number,
		perPage: number,
		sort: string
	): Promise<{ users: UserWithStats[]; totalItems: number }> {
		const filters = [`tenant = {:tenantId}`];
		const filterParams: Record<string, string> = { tenantId };

		if (search.trim()) {
			const words = search.trim().split(/\s+/);
			words.forEach((word, i) => {
				filters.push(`(first_name ~ {:q${i}} || last_name ~ {:q${i}} || email ~ {:q${i}})`);
				filterParams[`q${i}`] = word;
			});
		}

		const result = await this.collection<UserWithStats>(COL_ANALYTICS_USERS).getList(
			page,
			perPage,
			{
				filter: this.filter(filters.join(' && '), filterParams),
				sort,
				fields:
					'id,first_name,last_name,email,avatar,role,plan,user_created,conversation_count,message_count,token_count,last_activity'
			}
		);

		return { totalItems: result.totalItems, users: result.items };
	}

	chatRequest(
		conversationId: string,
		messages: ChatMessages,
		model: string,
		tenant: string,
		signal?: AbortSignal,
		webSearchEnabled?: boolean
	) {
		return fetch(new URL('/api/chat', this.baseURL), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {})
			},
			credentials: this.apiKey ? 'omit' : 'include',
			signal,
			body: JSON.stringify({
				conversationId,
				messages,
				model,
				tenant,
				...(webSearchEnabled ? { web_search_enabled: true } : {})
			})
		});
	}
}

export const pbService = new PbService();
